import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

const DRAWER_EXIT_MS = 280;

export interface ViewportDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Called after the close animation finishes. */
  onExited?: () => void;
  children: React.ReactNode;
  side?: 'right' | 'left';
  width?: string;
  className?: string;
  panelClassName?: string;
}

export function ViewportDrawer({
  open,
  onClose,
  onExited,
  children,
  side = 'right',
  width = 'min(420px, 92vw)',
  className,
  panelClassName,
}: ViewportDrawerProps) {
  const [mounted, setMounted] = useState(open);
  const [exiting, setExiting] = useState(false);
  const onExitedRef = useRef(onExited);
  const wasOpenRef = useRef(open);

  onExitedRef.current = onExited;

  useEffect(() => {
    if (open) {
      setMounted(true);
      setExiting(false);
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      setExiting(true);
    }
  }, [open]);

  useEffect(() => {
    if (!exiting) return;
    const timer = window.setTimeout(() => {
      setMounted(false);
      setExiting(false);
      onExitedRef.current?.();
    }, DRAWER_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [exiting]);

  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [mounted, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={cn('fixed inset-0 z-[var(--z-drawer)] h-dvh w-screen', className)}
      role="presentation"
    >
      <div
        className={cn(
          'cal-scrim absolute inset-0 bg-[rgba(36,41,54,0.35)]',
          exiting && 'cal-scrim-out',
        )}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'cal-drawer absolute top-0 flex h-dvh max-h-dvh flex-col border-[var(--cream-2)] bg-[var(--cream-0)] shadow-[var(--sh-3)]',
          exiting && 'cal-drawer-out',
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
          panelClassName,
        )}
        style={{ width }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
